<script lang="ts">
    import tabIcon from "@/assets/operationskoordinator.svg";

    export default {
        name: "Operationsoversikt",
        tabName: "Operationsöversikt",
        icon: tabIcon,
        refreshTab: true,
    };
</script>

<script setup lang="ts">
    import { CellContentWidth } from "@/components/KTable/helpers";
    import KTable from "@/components/KTable/KTable.vue";
    import { Column, KTableAction, TableValue } from "@/components/KTable/types";
    import ButtonLink from "@/components/ButtonLink.vue";
    import OperationsoversiktFilter from "@/tabs/Operation/OperationsoversiktFilter.vue";
    import CellStatus from "@/tabs/Operation/Operationsoversikt/CellStatus.vue";
    import { useStore } from "@/store";
    import { openDialog } from "@/utils";
    import { computed, onMounted, onUnmounted, ref } from "vue";
    import moment from "moment";
    import emitter from "tiny-emitter/instance";
    import ActionIconPagaende from "@/assets/actionIcons/pagaendeop.svg";
    import ActionIconOpanmalan from "@/assets/actionIcons/opanmalan.svg";
    import ActionIconSkapapreanestesibedomning from "@/assets/actionIcons/skapapreanestesibedomning.svg";
    import ActionIconShow from "@/assets/actionIcons/show.svg";
    import Operationsjournal from "@/tabs/Operation/Operationsjournal.vue";
    import Preanestesibedomning from "@/tabs/Operation/Preanestesibedomning.vue";
    import PreanestesibedomningVisa from "@/tabs/Operation/PreanestesibedomningVisa.vue";
    import Operationsinformation from "@/tabs/Operation/Operationsinformation.vue";
    import Dagsoversikt from "@/tabs/Operation/Oversikt/Dagsoversikt.vue";
    import Veckooversikt from "@/tabs/Operation/Oversikt/Veckooversikt.vue";
    import { OperationConfigModuleName } from "@/store/modules/types/configModuleTypes";

    const store = useStore();

    // Destructuring på props tar bort dess reaktivitet i Vue2. This is as intended
    // eslint-disable-next-line vue/no-setup-props-destructure
    const { tabId, initialFilterParams, initialOverrideDefaultFilter } = defineProps<{
        tabId: number;
        initialFilterParams?: Record<string, unknown>;
        initialOverrideDefaultFilter?: boolean;
    }>();

    const filterRef = ref();

    const can = (props: { action: string; subject: string }) => store.getters["ability/can"](props);

    const PREANESTESI_GODKANNANDE_STATUSAR = [
        "Komplettering krävs",
        "Komplettering utförd",
        "Godkänd",
        "Ej godkänd",
        "Utgången",
        "Återkallad",
    ];

    const columns: Column[] = [
        {
            field: "personId",
            header: "PersonID",
            sortField: "patient__personId",
            frozen: true,
            alignFrozen: "left",
            limitWidth: true,
            minWidth: CellContentWidth.custom({ amount: 8 }),
            sortable: true,
        },
        {
            field: "patientnamn",
            header: "Patientnamn",
            sortField: "patient__namn",
            sortable: true,
        },
        {
            field: "operator",
            header: "Operatör",
            sortField: "current_huvudoperator",
            sortable: true,
        },
        {
            field: "assOperatorer",
            header: "Ass. operatörer",
            contentType: "array",
            contentDirection: "vertical",
        },
        {
            field: "opdatum",
            header: "Opdatum",
            sortField: "bokad_operation_datum",
            contentType: "date",
            sortable: true,
        },
        {
            field: "optid",
            header: "Optid",
            sortField: "current_operation_tid",
            sortable: true,
        },
        {
            field: "opkod",
            header: "Opkod",
            sortField: "current_huvudoperationskod__code",
            sortable: true,
        },
        {
            field: "typoperation",
            header: "Typoperation",
            sortField: "current_typoperation__namn",
            sortable: true,
        },
        {
            field: "sal",
            header: "Sal",
            sortField: "current_sal__ordning",
            sortable: true,
        },
        {
            field: "opnr",
            header: "Opnummer",
            sortField: "opnr",
            sortable: true,
        },
        {
            field: "vardform",
            header: "Vårdform",
            sortField: "planerad_vardform__namn",
            sortable: true,
        },
        {
            field: "status",
            header: "Status",
            sortField: "status__sortering",
            frozen: true,
            alignFrozen: "right",
            bodyTemplate: CellStatus,
            limitWidth: true,
            minWidth: CellContentWidth.custom({ amount: 14 }),
            sortable: true,
        },
    ];

    type OversiktRow = TableValue & {
        patient?: string;
        status?: string;
        bokad_operation_datum?: string;
        aktuell_preanestesibedomning?: { id: string } | null;
        preanestesibedomning_godkannande?: string;
    };

    const openJournal = async (data: OversiktRow) => {
        if (!can({ action: "read", subject: "operationsjournal" })) return;
        await store.dispatch("tabs/closeTab", Operationsjournal, { root: true });
        await store.dispatch("patientData/loadFromBackend", { id: data.patient });
        await store.dispatch("tabs/openTab", {
            component: Operationsjournal,
            parameters: { operationsanmalan_id: data.id },
        });
    };

    const dropdownActions: KTableAction[] = [
        {
            title: "Visa operationsinformation",
            icon: ActionIconOpanmalan,
            show: () => can({ action: "read", subject: "dagsinformation" }),
            callback: async ({ data }) => {
                const row = data as OversiktRow;
                if (!can({ action: "read", subject: "dagsinformation" })) return;
                await store.dispatch("tabs/closeTab", Operationsinformation, { root: true });
                await store.dispatch("patientData/loadFromBackend", { id: row.patient });
                await store.dispatch("tabs/openTab", {
                    component: Operationsinformation,
                    parameters: { operationsanmalan_id: row.id },
                });
            },
        },
        {
            title: "Opstart i opjournal",
            icon: ActionIconPagaende,
            show: (item) => {
                const row = item as OversiktRow;
                return (
                    row.status === "Bokad" &&
                    Boolean(moment(row.bokad_operation_datum).isSameOrBefore(moment(), "day")) &&
                    can({ action: "read", subject: "operationsjournal" })
                );
            },
            callback: ({ data }) => openJournal(data as OversiktRow),
        },
        {
            title: "Redigera operationsjournal",
            icon: ActionIconPagaende,
            show: (item) => {
                const row = item as OversiktRow;
                return (
                    ["Pågående", "Genomförd"].includes(row.status ?? "") &&
                    can({ action: "read", subject: "operationsjournal" })
                );
            },
            callback: ({ data }) => openJournal(data as OversiktRow),
        },
        {
            title: "Granska operationsjournal",
            icon: ActionIconPagaende,
            show: (item) => {
                const row = item as OversiktRow;
                return row.status === "Signerad" && can({ action: "read", subject: "operationsjournal" });
            },
            callback: ({ data }) => openJournal(data as OversiktRow),
        },
        {
            title: "Redigera preanestesibedömning",
            icon: ActionIconSkapapreanestesibedomning,
            show: (item) => {
                const row = item as OversiktRow;
                return (
                    !!row.aktuell_preanestesibedomning &&
                    PREANESTESI_GODKANNANDE_STATUSAR.includes(row.preanestesibedomning_godkannande ?? "") &&
                    can({ action: "create", subject: "preanestesibedomning" })
                );
            },
            callback: async ({ data }) => {
                const row = data as OversiktRow;
                if (!can({ action: "create", subject: "preanestesibedomning" })) return;
                await store.dispatch("tabs/closeTab", Preanestesibedomning, { root: true });
                await store.dispatch("patientData/loadFromBackend", { id: row.patient });
                await store.dispatch("tabs/openTab", {
                    component: Preanestesibedomning,
                    parameters: {
                        operationsanmalanId: row.id,
                        preanestesibedomningId: row.aktuell_preanestesibedomning?.id,
                    },
                });
            },
        },
        {
            title: "Visa preanestesibedömning",
            icon: ActionIconShow,
            show: (item) => {
                const row = item as OversiktRow;
                return (
                    !!row.aktuell_preanestesibedomning &&
                    PREANESTESI_GODKANNANDE_STATUSAR.includes(row.preanestesibedomning_godkannande ?? "") &&
                    can({ action: "read", subject: "preanestesibedomning" })
                );
            },
            callback: async ({ data }) => {
                const row = data as OversiktRow;
                await store.dispatch("tabs/openTab", {
                    component: PreanestesibedomningVisa,
                    parameters: {
                        preanestesibedomningId: row.aktuell_preanestesibedomning?.id,
                        operationsanmalanId: row.id,
                        showPreanestesibedomning: true,
                    },
                });
            },
        },
    ];

    const listPage = ref<number>(1);
    const listCount = ref<number>(0);
    const listLimit = ref<number>(JSON.parse(sessionStorage.getItem("operationsoversikt-table") ?? "{}")?.rows ?? 50);
    const isLoading = ref(false);

    const first = computed<number>(() => (listPage.value - 1) * listLimit.value);

    const operationsoversiktDefaultFilter = computed<
        { Operationsdatum?: boolean; Operationsenhet?: boolean } | undefined
    >(() => store.state.config?.operation?.[OperationConfigModuleName.OperationsoversiktDefaultFilter]);

    const defaultOperationsenhetFilter = computed<{ operationsenhet?: Array<{ value: unknown; label: string }> }>(
        () => {
            const shouldFilterOperationsenhet = operationsoversiktDefaultFilter.value?.Operationsenhet ?? false;
            const currentProfile = store.getters["userData/currentProfile"];
            const orgUnit = currentProfile?.orgUnit;
            if (shouldFilterOperationsenhet && orgUnit?.vardenhetstyp === "operationsenhet") {
                return {
                    operationsenhet: [{ value: orgUnit.id, label: orgUnit.name ?? orgUnit.alias }],
                };
            }
            return {};
        }
    );

    // isoWeek ger en vecka måndag–söndag oavsett vilken locale moment kör med
    const defaultDatumFilter = computed<Record<string, unknown>>(() => {
        if (operationsoversiktDefaultFilter.value?.Operationsdatum ?? true) {
            return {
                bokad_operation_datum__gte: moment().format("YYYY-MM-DD"),
                bokad_operation_datum__lte: moment().endOf("isoWeek").format("YYYY-MM-DD"),
            };
        }
        return {};
    });

    const defaultFilter = computed<{ [key: string]: unknown }>(() => {
        const base = {
            ...defaultDatumFilter.value,
            ...defaultOperationsenhetFilter.value,
        };

        if (initialFilterParams) {
            if (initialOverrideDefaultFilter) {
                return { ...initialFilterParams };
            }
            return { ...base, ...initialFilterParams };
        }

        return base;
    });

    // show_strukna hanteras separat från filterformuläret (checkbox i tabellhuvudet), precis som tidigare.
    const showStrukna = ref<boolean>(!!initialFilterParams?.show_strukna);
    const filterParams = ref<{ [key: string]: unknown }>({ ...defaultFilter.value });

    const oversikter = computed<OversiktRow[]>(() => {
        const subset = store.getters["operation/operationoversikt/oversiktSubset"]({
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        });

        return subset.map((anmalan: Record<string, any>) => ({
            id: anmalan.id,
            patient: anmalan.patient,
            personId: anmalan.patient_display?.personId_formatted,
            patientnamn: anmalan.patient_display?.namn_reverse,
            operator: anmalan.current_huvudoperator_display?.user_display?.name,
            assOperatorer: anmalan.assoperator_ej_bestamd
                ? ["Ej bestämd"]
                : (anmalan.current_ass_operatorer_display ?? [])
                      .map((operator: Record<string, any>) => operator.user_display?.name)
                      .filter(Boolean),
            opdatum: anmalan.bokad_operation_datum,
            optid: anmalan.current_operation_tid
                ? moment(anmalan.current_operation_tid, "HH:mm:ss").format("HH:mm")
                : "",
            opkod: anmalan.current_huvudoperationskod_display?.code,
            typoperation: anmalan.current_typoperation_display?.namn,
            sal: anmalan.current_sal_display?.namn,
            opnr: anmalan.opnr,
            vardform: anmalan.planerad_vardform_display?.namn,
            status: anmalan.status,
            bokad_operation_datum: anmalan.bokad_operation_datum,
            aktuell_preanestesibedomning: anmalan.aktuell_preanestesibedomning,
            preanestesibedomning_godkannande: anmalan.preanestesibedomning_godkannande,
        }));
    });

    const fetchOversikter = async ({ page, limit = listLimit.value }: { page: number; limit?: number }) => {
        isLoading.value = true;
        try {
            const { total } = await store.dispatch("operation/operationoversikt/fetchOversikter", {
                page,
                limit,
                filterParams: filterParams.value,
            });

            listPage.value = page;
            if (typeof total === "number") {
                listCount.value = total;
            }
        } catch (error) {
            console.error(error);
            openDialog("Kunde inte hämta operationsanmälningar", "error");
        } finally {
            isLoading.value = false;
        }
    };

    const onFilter = ({ value }: { value: Record<string, unknown> }) => {
        filterParams.value = { ...value, show_strukna: showStrukna.value };
        fetchOversikter({ page: 1 });
    };

    const onPage = ({ page, rows }: { page: number; rows: number }) => {
        listLimit.value = rows;
        fetchOversikter({ page, limit: rows });
    };

    const onSort = async ({ sortField, sortOrder }: { sortField: string; sortOrder: number }) => {
        if (sortField) {
            const prefix = sortOrder > 0 ? "" : "-";
            let ordering = `${prefix}${sortField}`;
            // Sortera sekundärt på optid när primär sortering sker på opdatum. Optid är alltid stigande så att
            // operationerna hamnar i kronologisk tidsordning inom dagen, oavsett opdatums riktning.
            if (sortField === "bokad_operation_datum") {
                ordering += ",current_operation_tid";
            }
            filterRef?.value?.setFieldValue?.({ name: "ordering", value: ordering });
        } else {
            filterRef?.value?.setFieldValue?.({ name: "ordering", value: null });
        }

        const data = await filterRef?.value?.manualSubmit?.();
        if (data) {
            onFilter({ value: data });
        }
    };

    const onVisaStrukna = async ({ value }: { value: boolean }) => {
        showStrukna.value = value;
        const data = await filterRef?.value?.manualSubmit?.();
        onFilter({ value: data ?? filterParams.value });
    };

    const onRowClick = ({ data }: { data: TableValue }) => {
        if (data.patient) {
            store.dispatch("patientData/loadFromBackend", {
                id: data.patient,
                failSilently: false,
            });
        }
    };

    const dagsoversikt = async () => {
        await store.dispatch("tabs/closeTab", Dagsoversikt, { root: true });
        await store.dispatch("tabs/openTab", {
            component: Dagsoversikt,
            parameters: {
                date: (filterParams.value.bokad_operation_datum__gte as string) ?? moment().format("YYYY-MM-DD"),
            },
        });
    };

    const veckooversikt = async () => {
        await store.dispatch("tabs/closeTab", Veckooversikt, { root: true });
        await store.dispatch("tabs/openTab", {
            component: Veckooversikt,
            parameters: {},
        });
    };

    const onEntryUppdaterad = async (anmalan: { id?: string }) => {
        if (!anmalan?.id) return;
        if (!oversikter.value.some((row) => row.id === anmalan.id)) return;
        try {
            await store.dispatch("operation/operationoversikt/getOversiktById", {
                id: anmalan.id,
                forceRefresh: true,
            });
        } catch (error) {
            openDialog(error, "error");
        }
    };

    const onRefreshTab = async () => {
        const data = await filterRef?.value?.manualSubmit?.();
        filterParams.value = { ...(data ?? filterParams.value), show_strukna: showStrukna.value };
        await fetchOversikter({ page: 1 });
    };

    onMounted(() => {
        if (!can({ action: "read", subject: "operationsoversikt" })) {
            store.dispatch("tabs/closeTab", tabId);
            return;
        }

        store.dispatch("operation/operationoversikt/addSubscriber");

        emitter.on("operationsanmalan-uppdaterad", onEntryUppdaterad);
        emitter.on("operationsanmalan-bokad", onEntryUppdaterad);
        emitter.on("operationsanmalan-avbokad", onEntryUppdaterad);
        emitter.on(`refreshTab-${tabId}`, onRefreshTab);
    });

    onUnmounted(() => {
        store.dispatch("operation/operationoversikt/removeSubscriber");
        emitter.off("operationsanmalan-uppdaterad", onEntryUppdaterad);
        emitter.off("operationsanmalan-bokad", onEntryUppdaterad);
        emitter.off("operationsanmalan-avbokad", onEntryUppdaterad);
        emitter.off(`refreshTab-${tabId}`);
    });
</script>

<template>
    <div class="operationsoversikt">
        <div class="header">
            <h1>Operationsöversikt</h1>
            <ButtonLink @click="dagsoversikt" :disabled="!can({ action: 'read', subject: 'dagsoversikt' })">
                Dagöversikt utskrivbar
            </ButtonLink>
            <ButtonLink @click="veckooversikt" :disabled="!can({ action: 'read', subject: 'veckooversikt' })">
                Veckoöversikt utskrivbar
            </ButtonLink>
        </div>
        <OperationsoversiktFilter
            ref="filterRef"
            tableStateName="operationsoversikt-table"
            :keepOnClear="['ordering']"
            :filterOnMount="true"
            :defaultValue="filterParams"
            @submit="onFilter"
        />
        <KTable
            reorderableColumns
            togglableColumns
            paginator
            alwaysShowPaginator
            useState
            loadingMessage="Hämtar operationsanmälningar"
            paginatorPosition="bottom"
            name="operationsoversikt-table"
            :columns="columns"
            :actions="dropdownActions"
            :value="oversikter"
            :totalRecords="listCount"
            :loading="isLoading"
            :unselectable="true"
            :first="first"
            @rowClick="onRowClick"
            @menuClick="onRowClick"
            @page="onPage"
            @sort="onSort"
        >
            <template #header>
                <KCheckbox
                    name="show_strukna"
                    label="Visa även strukna operationer"
                    valueType="boolean"
                    :value="showStrukna"
                    @inputValue="onVisaStrukna"
                />
            </template>
        </KTable>
    </div>
</template>

<style lang="scss">
    // För att se till att höjden på containern sträcker ut sig korrekt så att tabellen kan anpassa sig efter
    // tillgänglig yta
    #tab-container .tab-containers section:has(.operationsoversikt) {
        > div:first-child {
            height: 100%;
        }
    }
</style>

<style lang="scss" scoped>
    @import "@/style/_variables.scss";
    @import "@/style/container";

    .operationsoversikt {
        width: $tab-wider-width;
        max-width: 100%;
        height: 100%;
        display: flex;
        flex-flow: column;

        .header {
            display: flex;
            align-items: center;
            gap: 20px;
            margin-bottom: 26px;

            h1 {
                margin: 0;
            }
        }

        // För att låta tabellen växa automatiskt utifrån tillgängligt utrymme
        ::v-deep .k-table {
            min-height: 30rem;
        }
    }
</style>
